import { useField } from "formik";
import { useRef } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import { SxProps, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";

import FilterStyles from "../../FilterControls/FilterStyles";

export type OptionType = { id: string; label: string } | number | string;

interface IAutocompleteFilterProperties<T> {
  color?: "primary" | "secondary";
  isDisabled?: boolean;
  isDisablePortal?: boolean;
  isFreeSolo?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  isMultiple?: boolean;
  isRequired?: boolean;
  label?: string;
  limitTags?: number;
  name: string;
  onBlur?: (newOption: T[]) => void;
  onChange?: (newOption: T[]) => void;
  onFocus?: (newOption: T[]) => void;
  options?: T[];
  shouldAutoComplete?: boolean;
  size?: "medium" | "small";
  sx?: SxProps<Theme>;
  type: string;
  value?: T | T[];
}

export default function AutocompleteFilter<T extends OptionType>({
  color = "secondary",
  isDisabled = false,
  isDisablePortal = true,
  isFreeSolo = true,
  isFullWidth = false,
  isLoading = false,
  isMultiple = false,
  isRequired = false,
  label,
  limitTags = -1,
  name,
  onChange = () => {
    /* Do Nothing */
  },
  options = [],
  shouldAutoComplete = false,
  size = "medium",
  sx,
  type,
  value,
}: IAutocompleteFilterProperties<T>) {
  const [field, meta, helpers] = useField<string | T | T[]>(name);

  // Define a ref to hold the input element
  const inputReference = useRef<HTMLInputElement | null>(null);

  const handleChange = (newValue: T[]) => {
    onChange(newValue);
  };

  const handleBlur = () => {
    // Simulate pressing the Enter key
    const enterEvent = new KeyboardEvent("keydown", {
      bubbles: true,
      cancelable: true,
      code: "Enter",
      key: "Enter",
    });

    inputReference.current?.dispatchEvent(enterEvent);
  };

  const getTextFieldValue = (option: T | T[]) => {
    if (typeof option === "string") return option;
    else if (typeof option === "number") return option.toString();
    else if (option && typeof option === "object" && "label" in option) return option.label;
    return "";
  };

  return (
    <Autocomplete
      autoComplete={shouldAutoComplete}
      disabled={isDisabled}
      disablePortal={isDisablePortal}
      freeSolo={isFreeSolo}
      fullWidth={isFullWidth}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        else if (typeof option === "number") return option.toString();
        else if (option && typeof option === "object" && "label" in option) return option.label;
        return "";
      }}
      limitTags={limitTags}
      loading={isLoading}
      multiple={isMultiple}
      onBlur={handleBlur}
      onChange={(_, newOption) => {
        if (!newOption) return;
        handleChange(newOption as T[]);
        void helpers.setValue(newOption as T);
      }}
      options={options}
      renderInput={(parameters) => (
        <TextField
          {...parameters}
          color={color}
          disabled={parameters.disabled}
          error={!!(meta.touched && meta.error)}
          fullWidth={parameters.fullWidth}
          helperText={meta.touched && meta.error}
          id={parameters.id}
          inputRef={inputReference} // Attach the ref to the input element
          label={label}
          margin="dense"
          name={name}
          required={isRequired}
          size={parameters.size}
          type={type}
          value={getTextFieldValue(field.value as T)}
          variant="outlined"
        />
      )}
      size={size}
      sx={{
        ...FilterStyles.autoComplete,
        ...sx,
        radius: "12px",
      }}
      value={value ?? []}
    />
  );
}
